import React, { useMemo } from 'react';
import FrontLayout from '@layouts/FrontLayout';
import HeroBanner from '@components/presentational/Banners/HeroBanner';
import FormationPicto from '@/components/presentational/img/icons/FormationPicto';
import {
  PiListChecksThin,
  PiMagnifyingGlassThin,
  PiVideoCameraThin,
} from 'react-icons/pi';
import { NextPageContext } from 'next';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Show,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import UIWidth from '@components/presentational/UIWidth';
import NextLink from 'next/link';
import VolDeMouettesFormations from '@/components/presentational/img/v2024/VolDeMouettesFormations.svg';
import BgMasqueHaut from '@/components/presentational/img/v2024/BgMasqueHaut.svg';
import CourseListItem from '@components/course/CourseListItem';
import ImageBanner from '@components/presentational/Banners/ImageBanner';
import { SearchResult } from '@/types/search/searchresult';
import Header, { NavCategory, NavSubCategory } from '@components/Header';
import { Course } from '@/types/core/Course';
import { getUserCoursesSessions } from '@/api/courseSession';
import { getCourses } from '@/api/course';
import { CourseSession } from '@/types/core/CourseSession';
import withUser from '@/withUser';
import Head from 'next/head';
import Breadcrumb, { BreadcrumbItem } from '@components/app/Breadcrumb';
import FormationsHero from '../../../app/components/presentational/img/v2024/formations/FormationsHero.webp';
import FormationsWebinaires from '../../../app/components/presentational/img/v2024/formations/FormationsWebinaires.webp';
import FormationsTestsLangue from '../../../app/components/presentational/img/v2024/formations/FormationsTestsLangue.webp';
import Carousel from '@/components/refonte2024/app/Carousel';
import getConfig from 'next/config';
import IdeaPicto from '@/components/presentational/img/icons/IdeaPicto';
import TextBanner from '@components/presentational/Banners/TextBanner';

const SliderIcon = ({ icon }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="3rem"
      height="3rem"
      borderRadius="full"
      background="white"
      border="1px solid"
      borderColor="menthe.100"
      cursor="pointer"
    >
      <Icon as={icon} boxSize="2rem" color="menthe.100" />
    </Box>
  );
};

function PageFormations({
  courses,
  coursesSessions,
}: {
  courses: SearchResult<Course>;
  coursesSessions: CourseSession[];
}) {
  const expertiseEnabled = useMemo<boolean>(() => {
    return (
      getConfig().publicRuntimeConfig.EXPERTISE_DISPLAY_FRONT &&
      getConfig().publicRuntimeConfig.EXPERTISE_ENABLED
    );
  }, [getConfig().publicRuntimeConfig]);

  return (
    <FrontLayout
      gtmPage={{
        page_type: 'Formation',
        page_level1: 'Les formations IFprofs',
      }}
    >
      <Head>
        <>
          <title>
            Formations pédagogiques FLE et webinaires pour enseignants | IFprofs
          </title>
          <meta
            key={'description'}
            name="description"
            content="Découvrez des formations certifiantes et des webinaires enrichissants sur IFprofs pour développer vos compétences pédagogiques et enseigner le français facilement. Testez votre niveau de français et accédez à un réseau d'experts reconnus."
          />
        </>
      </Head>
      <Header
        mainNavItemActive={NavCategory.Formations}
        subNavItemActive={NavSubCategory.COURSES}
      />
      <HeroBanner
        title="Des formations pour consolider vos compétences d'enseignement"
        text="La formation continue est essentielle dans les métiers de l'enseignement. C'est pourquoi IFprofs a créé des modules d'auto-formation FLE sur des thématiques variées et répondant aux problématiques actuelles pour apprendre et enseigner le français, des débutants aux bilingues."
        image={FormationsHero.src}
        accentColor="menthe.100"
        textColor="black"
        pictoTopBase="19.5rem"
        pictoTopSm="5rem"
        pictoLeftBase="-23.5rem"
        picto={
          <>
            <Show breakpoint="(min-width: 48em)">
              <FormationPicto
                freeIconColor="white"
                iconAlt=""
                iconWidth="20em"
              />
            </Show>
            <Show breakpoint="(max-width: 47em)">
              <FormationPicto
                freeIconColor="white"
                iconAlt=""
                iconWidth="60em"
              />
            </Show>
          </>
        }
      />
      <Breadcrumb
        breadcrumbItems={[
          { label: 'Accueil', href: '/' },
          { label: 'Les formations IFprofs' },
        ]}
      >
        <BreadcrumbItem label="Accueil" as={'/'} href={'/'} />
        <BreadcrumbItem label="Les formations IFprofs" />
      </Breadcrumb>

      <Box
        as="section"
        mt="2rem"
        backgroundImage={{ md: `url("${BgMasqueHaut.src}")` }}
        backgroundRepeat={{ md: 'no-repeat' }}
        backgroundPosition={{
          md: '0 rem',
        }}
        backgroundSize={{ md: '512rem auto' }}
        backgroundColor={{ md: 'menthe.25' }}
      >
        <Box
          backgroundImage={{ md: `url("${VolDeMouettesFormations.src}")` }}
          backgroundSize={{ md: '100% auto' }}
          backgroundRepeat={{ md: 'no-repeat' }}
          backgroundPosition={{
            md: '0 rem',
          }}
          pb="2rem"
        >
          <UIWidth>
            <Flex
              direction="column"
              p={['1.25rem 0 2.5rem 0', '', '0.5rem 0 5.9rem 0']}
            >
              <Heading
                as="h2"
                color="brand.100"
                fontSize={['2rem', '', '3.1875rem']}
                mb={['1.4rem', '', '2.8rem']}
              >
                Les formations IFprofs
              </Heading>
              <Text
                fontSize={['1rem', '', '1.25rem']}
                mb={['1.5rem', '', '4.5rem']}
              >
                Découvrez nos formations à distance spécialement conçues pour
                vous, avec des experts FLE de chaque domaine. Vous pouvez suivre
                les modules en autonomie, à votre rythme, et obtenir une
                attestation en fin de parcours.
              </Text>
              <Carousel variant={'menthe'}>
                {courses?.items &&
                  courses.items.map(course => (
                    <CourseListItem
                      key={course.id}
                      course={course}
                      coursesSessions={coursesSessions}
                    />
                  ))}
              </Carousel>
            </Flex>
          </UIWidth>
        </Box>
      </Box>

      <ImageBanner
        title="Les webinaires IFprofs"
        text="IFprofs organise régulièrement des webinaires avec pour objectif de vous accompagner sur vos pratiques pédagogiques et sur des sujets d'actualités."
        image={FormationsWebinaires.src}
        accentColor="menthe.100"
      >
        <NextLink href={'/webinars/replays'} passHref>
          <Button as="a" variant="mintOutline" size={['sm', '', 'md']}>
            <Icon as={PiVideoCameraThin} boxSize={6} mr={2} />
            Voir les replays
          </Button>
        </NextLink>
      </ImageBanner>

      <ImageBanner
        title="Un doute sur votre niveau de français ? Testez-vous."
        text="IFprofs a listé des sites proposant des tests de positionnement pour évaluer votre niveau de français (A2, B1, B2, C1, C2). Cela vous permettra ensuite de sélectionner une formation adaptée."
        image={FormationsTestsLangue.src}
        accentColor="menthe.25"
        imageFirst
      >
        <NextLink href={'/formations/test-niveau-de-langue'} passHref>
          <Button as={'a'} variant="mintOutline" size={['sm', '', 'md']}>
            <Icon as={PiListChecksThin} boxSize={6} mr={2} />
            Consulter la liste des tests{' '}
            <VisuallyHidden>en ligne</VisuallyHidden>
          </Button>
        </NextLink>
      </ImageBanner>

      {expertiseEnabled && (
        <TextBanner
          title="Besoin d'une expertise en formation ou en ingénierie pédagogique ?"
          text="IFprofs vous propose un annuaire d'experts pour répondre à vos besoins d'intervention dans différents domaines : formation pédagogique, conception de ressources, ingénierie de formation, éducation aux médias, etc."
          accentColor="menthe.100"
          button={
            <NextLink href="/formations/annuaire-des-experts" passHref>
              <Button as="a" variant="mintOutline" size={['sm', '', 'md']}>
                <Icon as={PiMagnifyingGlassThin} boxSize={6} mr={2} />
                Trouver votre expert
              </Button>
            </NextLink>
          }
          picto={
            <IdeaPicto freeIconColor="white" iconAlt="" iconWidth="12.5em" />
          }
        />
      )}
    </FrontLayout>
  );
}

PageFormations.getInitialProps = async (context: NextPageContext) => {
  const courses = await getCourses({ context });
  let coursesSessions: CourseSession[] = [];
  try {
    coursesSessions = await getUserCoursesSessions({ context });
  } catch (error) {}

  return {
    courses,
    coursesSessions,
  };
};

export default withUser(PageFormations);
